import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { SlidersHorizontal, Settings, User } from "lucide-react";
import { Table, ConfigProvider } from "antd";
import type { ColumnsType } from "antd/es/table";
import Layout from "../../Layout";

interface ReportItem {
  key: number;
  id: number;
  title: string;
  viewer: string | null;
  avatarUrl?: string;
  link?: string;
}

const reportData: ReportItem[] = [
  {
    key: 1,
    id: 1,
    title: "Xuất nhập tồn",
    viewer: "--",
    link: 'inventory',
  },
  {
    key: 2,
    id: 2,
    title: "Báo cáo công nợ",
    viewer: "avatar",
    avatarUrl: "https://api.dicebear.com/7.x/bottts/svg?seed=1",
    link: 'debt',
  },
];

export default function ReportList() {
  const { t } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  };

  const columns: ColumnsType<ReportItem> = [
    {
      title: "Tiêu đề",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <Link to={`/${record.link}`} className="font-medium text-gray-800 dark:text-gray-100">
          {text}
        </Link>
      ),
    },
    {
      title: "Người xem",
      dataIndex: "viewer",
      key: "viewer",
      align: "center",
      render: (_, record) => {
        if (record.viewer === "--") {
          return (
            <span className="text-slate-400 dark:text-slate-500">--</span>
          );
        }

        return record.avatarUrl ? (
          <img
            src={record.avatarUrl}
            alt="avatar"
            className="w-6 h-6 rounded object-cover mx-auto bg-orange-500 p-0.5"
          />
        ) : (
          <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center mx-auto">
            <User className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
          </div>
        );
      },
    },
  ];

  return (
    <Layout>
      <Helmet>
        <title>{t("page.repost_list")} | Khothi</title>
      </Helmet>

      {/* Header */}
      <div className="flex justify-between items-center mb-6 border-b border-gray-200 dark:border-gray-800 pb-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          { t('repost.title') }
        </h2>
      </div>

      {/* Toolbar giống AssetListAntd */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900">
        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-xs">
          <SlidersHorizontal className="w-4 h-4" />
          {selectedRowKeys.length > 0 ? (
            <span>
              Đã chọn <b>{ selectedRowKeys.length }</b> bản ghi
            </span>
          ): (
            <span>
              Hiển thị 1 - {reportData.length} / {reportData.length} bản ghi
            </span>
          )}
        </div>

        <button className="flex items-center gap-1.5 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100 transition-colors text-xs">
          <Settings className="w-4 h-4" />
          <span>Cài đặt</span>
        </button>
      </div>

      {/* Ant Design Table */}
      <div className="py-4">
        <ConfigProvider
          theme={{
            components: {
              Table: {
                headerBg: "#f8f9fa",
                headerColor: "#6b7280",
                cellPaddingBlock: 12,
                rowHoverBg: "#eff6ff",
              },
            },
          }}
        >
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={reportData}
            pagination={false}
            scroll={{ x: "max-content" }}
          />
        </ConfigProvider>
      </div>
    </Layout>
  );
}